// ============================================================================
// VOUCHER TYPE SERVICE (CRUD, entity-scoped via X-Entity-ID header)
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { voucherTypeEndpoints } from '../endpoints/accounts.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  VoucherTypeDto,
  CreateVoucherTypeRequest,
  UpdateVoucherTypeRequest,
  VoucherTypeQueryParams,
  VoucherClassDto,
} from '../types';

class VoucherTypeService {
  async getAll(params?: VoucherTypeQueryParams): Promise<PaginatedResponse<VoucherTypeDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(voucherTypeEndpoints.list(), params)
      : voucherTypeEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<VoucherTypeDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Voucher-class reference list (enum_master ACC_VOUCHER_CLASS) for dropdowns. */
  async getClasses(): Promise<VoucherClassDto[]> {
    const response = await get<ApiResponse<VoucherClassDto[]>>(voucherTypeEndpoints.classes());
    return response.data.data ?? [];
  }

  async getById(id: ID): Promise<ApiResponse<VoucherTypeDto>> {
    const response = await get<ApiResponse<VoucherTypeDto>>(voucherTypeEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateVoucherTypeRequest): Promise<ApiResponse<VoucherTypeDto>> {
    const response = await post<ApiResponse<VoucherTypeDto>>(voucherTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateVoucherTypeRequest): Promise<ApiResponse<VoucherTypeDto>> {
    const response = await put<ApiResponse<VoucherTypeDto>>(voucherTypeEndpoints.update(id), data);
    return response.data;
  }

  /** Soft-delete; rowVersion is required for optimistic-concurrency. */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(voucherTypeEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = voucherTypeEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const voucherTypeService = new VoucherTypeService();
export default voucherTypeService;
